import {
  CheckCircle,
  ChevronRight,
  Edit,
  Link,
  Mail,
  MoreHorizontal,
  UserPlus,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { SettingField } from "@/components/settings/setting-field";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { UserSalonRoleType } from "@/gql/graphql";
import { Can } from "@/lib/authorization";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { cn } from "@/lib/utils";
import { useTeamMembers } from "../hooks";
import { useTeamManagementUIStore } from "../store/use-team-management-ui-store";
import type { TeamMember } from "../types";
import { getTeamMemberDisplayName, getTeamMemberInitials } from "../utils/team-member-utils";
import { getPrimaryAction, getWorkerAccountStatus, getWorkerActions } from "../utils/worker-status";

interface TeamMemberCardProps {
  member: TeamMember;
}

export function TeamMemberCard({ member }: TeamMemberCardProps) {
  const { t } = useTranslation("settings");
  const currentUser = useAuthStore((state) => state.user);
  const {
    openEditDialog,
    openEmailChangeDialog,
    openLinkUserDialog: _openLinkUserDialog,
    openSetupAccountDialog,
    openServiceRestrictionDialog,
  } = useTeamManagementUIStore();
  const { toggleOnlineBooking, toggleActiveStatus, assignRole } = useTeamMembers();

  // Get display name and initials using utility functions
  const displayName = getTeamMemberDisplayName(member);
  const initials = getTeamMemberInitials(member);

  // Check if this is the current user
  const isCurrentUser = currentUser?.id === member.worker?.user?.id;

  // Use the first pending invitation from the worker's invitations array
  const invitation = member.worker?.invitations?.[0];

  // Get worker account status and available actions
  const accountStatus = getWorkerAccountStatus(member);
  const availableActions = getWorkerActions(member, accountStatus);
  const primaryAction = getPrimaryAction(accountStatus);

  // Determine email to display (user email or invitation email)
  const displayEmail = member.worker?.user?.email || invitation?.email;

  // Format hire date - TODO: Add hireDate field to WorkerDetailFragment
  // const hireDate = member.hireDate
  //   ? DateTime.fromISO(member.hireDate).toFormat('MMM dd, yyyy')
  //   : null;

  // Handle online booking toggle
  const handleOnlineBookingToggle = async () => {
    try {
      await toggleOnlineBooking(member);
    } catch (error) {
      console.error("Failed to toggle online booking:", error);
    }
  };

  // Handle active status toggle
  const handleActiveStatusToggle = async () => {
    try {
      await toggleActiveStatus(member);
    } catch (error) {
      console.error("Failed to toggle active status:", error);
    }
  };

  // Handle role assignment
  const handleRoleChange = async (roleValue: string) => {
    // Check if user account is linked
    if (!member.worker?.user?.id) {
      console.warn("Cannot assign role: No user account linked to this worker");
      return;
    }

    try {
      const roles: UserSalonRoleType[] = [roleValue as UserSalonRoleType];
      await assignRole(member, roles);
    } catch (error) {
      console.error("Failed to assign role:", error);
    }
  };

  // Handle invitation actions
  const handleChangeEmail = () => {
    openEmailChangeDialog(member.id);
  };

  const handleSetupAccount = () => {
    openSetupAccountDialog(member.id);
  };

  const handleCopyInvitationLink = () => {
    if (!invitation?.token) {
      toast.error(t("teamManagement.noInvitationFound"));
      return;
    }

    const inviteUrl = `${window.location.origin}/invite/${invitation.token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Invitation link copied to clipboard");
  };

  // Get current role for display
  const getCurrentRole = () => {
    // Return the WorkerSalon role directly
    return member.role;
  };

  // Role display mapping
  const getRoleDisplayName = (role: string | null) => {
    if (role === null) return t("teamManagement.noRoleAssigned");

    const roleMap: Record<string, string> = {
      WORKER: t("teamManagement.worker"),
      MANAGER: t("teamManagement.manager"),
      OWNER: t("teamManagement.owner"),
    };
    return roleMap[role] || role;
  };

  return (
    <Card>
      <CardHeader className="">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 flex-shrink-0">
            <AvatarImage src={undefined} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            {/* First Row: Name, Badge, and Buttons */}
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                <h3 className="font-semibold text-lg">{displayName}</h3>
                {!member.isActive && (
                  <Badge variant="secondary">{t("teamManagement.statusLabels.inactive")}</Badge>
                )}
              </div>

              <div className="flex items-start gap-2 flex-shrink-0">
                {/* Primary Action Buttons */}
                <Can I="update" a="Worker" passThrough>
                  {(canUpdate) =>
                    canUpdate && (
                      <>
                        {/* Setup Account Button - replaces Add Email and Link User */}
                        {availableActions.includes("setup_account") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSetupAccount}
                            className="gap-1"
                          >
                            <UserPlus className="w-3 h-3" />
                            <span className="hidden sm:inline">
                              {t("teamManagement.setupAccount")}
                            </span>
                          </Button>
                        )}
                      </>
                    )
                  }
                </Can>

                <Can I="update" a="Worker" passThrough>
                  {(canUpdate) =>
                    canUpdate && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(member.id)}>
                            <Edit className="w-4 h-4 mr-2" />
                            {t("actions.edit")}
                          </DropdownMenuItem>

                          {/* Active Status Toggle */}
                          <DropdownMenuItem
                            onClick={handleActiveStatusToggle}
                            className={
                              member.isActive ? "text-destructive focus:text-destructive" : ""
                            }
                          >
                            {member.isActive ? (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                {t("teamManagement.deactivate")}
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                {t("teamManagement.activate")}
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )
                  }
                </Can>
              </div>
            </div>

            {/* Second Row: Email and Primary Action */}
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                {displayEmail ? (
                  <>
                    <div className="flex items-center gap-1">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="break-all">{displayEmail}</span>
                    </div>
                    {invitation && !member.worker?.user?.email && (
                      <span className="text-xs text-amber-600">
                        ({t("teamManagement.invitationSent")})
                      </span>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{t("teamManagement.noEmail")}</span>
                  </div>
                )}
              </div>

              {/* Primary Action Suggestion */}
              {primaryAction && (
                <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-200 inline-block">
                  {primaryAction}
                </div>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {/* Settings */}
          <div className="space-y-3">
            <Can I="update" a="Worker" passThrough>
              {(canUpdate) => (
                <>
                  <Can I="manage" a="UserRole" passThrough>
                    {(_canManageRoles) => (
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <SettingField
                          label={t("teamManagement.role")}
                          helpText={t("descriptions.rolePermissions")}
                          className="flex-1"
                        >
                          <div />
                        </SettingField>
                        <Select
                          value={getCurrentRole() || ""}
                          onValueChange={handleRoleChange}
                          disabled={!canUpdate || isCurrentUser}
                        >
                          <SelectTrigger className="w-full sm:w-[120px]">
                            <SelectValue placeholder={t("teamManagement.selectRole")}>
                              {getRoleDisplayName(getCurrentRole())}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="WORKER">{t("teamManagement.worker")}</SelectItem>
                            <SelectItem value="MANAGER">{t("teamManagement.manager")}</SelectItem>
                            <SelectItem value="OWNER">{t("teamManagement.owner")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </Can>

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <SettingField
                      label={t("teamManagement.onlineBooking")}
                      helpText={t("descriptions.onlineBookingInfo")}
                      className="flex-1"
                    >
                      <div />
                    </SettingField>
                    <Switch
                      checked={member.canAcceptOnlineBookings}
                      onCheckedChange={handleOnlineBookingToggle}
                      disabled={!canUpdate || !member.isActive}
                    />
                  </div>

                  {/* Service Restrictions */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <SettingField
                      label={t("teamManagement.serviceRestrictions.label")}
                      helpText={t("descriptions.serviceRestrictionsInfo")}
                      className="flex-1"
                    >
                      <div />
                    </SettingField>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openServiceRestrictionDialog(member.id)}
                      disabled={!canUpdate}
                      className="w-full sm:w-auto justify-start gap-2"
                    >
                      <span className="text-xs flex-1">
                        {member.serviceRestrictionMode === "ALLOW_ALL" ? (
                          <span>{t("teamManagement.serviceRestrictions.canPerformAll")}</span>
                        ) : (
                          <span>
                            {(() => {
                              const categoryCount = member.allowedCategories?.length || 0;
                              const serviceCount = member.allowedServices?.length || 0;

                              // Show summary based on what's available
                              if (categoryCount > 0 && serviceCount > 0) {
                                return `${t("teamManagement.serviceRestrictions.categoriesCount", { count: categoryCount })}, ${t("teamManagement.serviceRestrictions.servicesCount", { count: serviceCount })}`;
                              } else if (categoryCount > 0) {
                                return t("teamManagement.serviceRestrictions.categoriesCount", {
                                  count: categoryCount,
                                });
                              } else if (serviceCount > 0) {
                                return t("teamManagement.serviceRestrictions.servicesCount", {
                                  count: serviceCount,
                                });
                              }
                              return "";
                            })()}
                          </span>
                        )}
                      </span>
                      <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    </Button>
                  </div>

                  {/* Invitation Email Section - Only show if email can be changed or invitation actions are available */}
                  {(availableActions.includes("change_email") ||
                    availableActions.includes("resend_invitation") ||
                    invitation?.token) && (
                    <div className="space-y-2">
                      {/* Invitation Email Label */}
                      <SettingField
                        label={t("teamManagement.invitationEmail")}
                        helpText={t("descriptions.changeEmailInfo")}
                      >
                        <div />
                      </SettingField>

                      {/* Invitation Actions */}
                      <ButtonGroup>
                        {/* Change Email Button */}
                        {availableActions.includes("change_email") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleChangeEmail}
                            disabled={!canUpdate}
                            className={cn("gap-1", invitation?.token && "rounded-r-none")}
                          >
                            <Mail className="w-3 h-3" />
                            {t("teamManagement.changeEmail")}
                          </Button>
                        )}

                        {/* Copy Invitation Link Button */}
                        {invitation?.token && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleCopyInvitationLink}
                            className={cn(
                              "gap-1",
                              availableActions.includes("change_email") && "rounded-l-none"
                            )}
                          >
                            <Link className="w-3 h-3" />
                            {t("teamManagement.copyInvitationLink")}
                          </Button>
                        )}
                      </ButtonGroup>
                    </div>
                  )}
                </>
              )}
            </Can>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
